/**
 * TableHeader  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { getBaseClass } from "./baseClass";
import { TABLE_SORT, TableCellAlign } from "./TableTypes";
import { TableSorter } from "./TableSorter";
import { isFunction } from "@adapptio/lib-shared";

export interface ITableHeaderProps {
	columnId: string;
	align?: TableCellAlign;
	scope?: "col" | "row";
	sortable?: boolean;
	onSort?: (columnId: string) => void;
	sortActive?: boolean;
	sortDirection?: TABLE_SORT;
}

export const TableHeader: React.FC<ITableHeaderProps> = ({
	children,
	columnId,
	align,
	scope,
	sortable,
	onSort,
	sortActive,
	sortDirection
}) => {
	const baseClass = getBaseClass("header-cell", { sortable, align });

	const handleSort = useCallback(() => {
		if (!sortable) {
			return;
		}

		if (isFunction(onSort)) {
			onSort(columnId);
		}
	}, [ columnId, onSort, sortable ]);

	return (
		<th onClick={handleSort} className={baseClass.toClassName()} scope={scope}>
			<div className={baseClass.element("header-cell-content").toClassName()}>
				<div className={baseClass.element("header-label").toClassName()}>{children}</div>
				{sortable && <TableSorter active={!!sortActive} direction={sortDirection} />}
			</div>
		</th>
	);
};

TableHeader.defaultProps = {
	align: "left",
	scope: "col"
};
